import React from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@common/hooks';
import { Menu, Dropdown } from 'antd';
import { Link, Modal } from '@common/components';

import {
  createMessage,
  mutationResponseMessage,
} from 'works/legacy/lib/message-helper';
import { PerformAssignmentAction } from 'works/legacy/graphql/staffing-request-assignment';
import {
  AssignmentStatePill,
  ASSIGNMENT_STATE_INFO,
} from 'works/legacy/components/assignment-state-pill';
import { buildDialogUrl } from '@common/services';

// Context: This component was not split due to a few limitations / bugs on Ant Design:
// https://stackoverflow.com/questions/53079845/use-custom-component-in-overlay-attribute-in-antd
// https://github.com/ant-design/ant-design/issues/4853

function State({ assignment, fontSize, statesMenu }) {
  const anyLeftTransition =
    assignment.canOnboard.value ||
    assignment.canViewClear.value ||
    assignment.canWork.value ||
    assignment.canComplete.value ||
    assignment.canCancel.value;

  const pill = () => (
    <AssignmentStatePill
      state={assignment.state}
      dropdown={anyLeftTransition}
      completedTasksCount={
        assignment.worksList && assignment.worksList.completedItemsCount
      }
      totalTasksCount={
        assignment.worksList && assignment.worksList.totalItemsCount
      }
      neverStarted={assignment.neverStarted}
      fontSize={fontSize}
    />
  );

  const menu = () => <Dropdown overlay={statesMenu}>{pill()}</Dropdown>;

  return anyLeftTransition ? menu() : pill();
}

State.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    neverStarted: PropTypes.bool.isRequired,
    worksList: PropTypes.shape({
      state: PropTypes.string,
      completedItemsCount: PropTypes.number,
      totalItemsCount: PropTypes.number,
    }),
  }),
  drawerVisible: PropTypes.bool.isRequired,
  fontSize: PropTypes.string,
  statesMenu: PropTypes.node.isRequired,
  onDrawerClose: PropTypes.func.isRequired,
  onDrawerOk: PropTypes.func.isRequired,
};

export function AssignmentStateMenu({ assignment, fontSize }) {
  const [mutationAction] = useMutation(PerformAssignmentAction, {
    refetchQueries: ['assignmentTerm'],
  });

  const onClick = ({ item }) => {
    const action = item.props.action;
    const isClearAction = action === ASSIGNMENT_STATE_INFO.cleared.action;

    if (isClearAction) {
      handleClearAction();
      return;
    }

    runMutationAction(action);
  };

  const handleClearAction = () => {
    const action = ASSIGNMENT_STATE_INFO.cleared.action;
    const hasAnyPendingItem =
      assignment.worksList.completedItemsCount !==
      assignment.worksList.totalItemsCount;

    if (!hasAnyPendingItem) {
      runMutationAction(action);
      return;
    }

    if (assignment.canOverrideClear.value) {
      confirmClearAssignmentWithPendingItems({
        onConfirm: () => runMutationAction(action),
      });
    } else {
      createMessage('error', 'All items must first be approved');
    }
  };

  const runMutationAction = (action) => {
    mutationAction({
      variables: {
        staffingRequestAssignmentId: assignment.id,
        input: {
          action: action,
        },
      },
    }).then(mutationResponse);
  };

  const mutationResponse = (response) =>
    mutationResponseMessage({
      gqlResponse: response,
      onSuccess: 'Assignment status successfully changed',
      errorMessage: 'Failed to update assignment status',
    });

  const cancelAssignmentDrawerUrl = buildDialogUrl('cancel-assignment-drawer', {
    props: { assignmentId: assignment.id },
  });

  const menu = (
    <Menu>
      {assignment.canOnboard.value && (
        <Menu.Item
          onClick={onClick}
          key={ASSIGNMENT_STATE_INFO.onboarding.label}
          action={ASSIGNMENT_STATE_INFO.onboarding.action}
        >
          {ASSIGNMENT_STATE_INFO.onboarding.label}
        </Menu.Item>
      )}
      {assignment.canViewClear.value && (
        <Menu.Item
          onClick={onClick}
          key={ASSIGNMENT_STATE_INFO.cleared.label}
          action={ASSIGNMENT_STATE_INFO.cleared.action}
        >
          {ASSIGNMENT_STATE_INFO.cleared.label}
        </Menu.Item>
      )}
      {assignment.canWork.value && (
        <Menu.Item
          onClick={onClick}
          key={ASSIGNMENT_STATE_INFO.working.label}
          action={ASSIGNMENT_STATE_INFO.working.action}
        >
          {ASSIGNMENT_STATE_INFO.working.label}
        </Menu.Item>
      )}
      {assignment.canComplete.value && (
        <Menu.Item
          onClick={onClick}
          key={ASSIGNMENT_STATE_INFO.completed.label}
          action={ASSIGNMENT_STATE_INFO.completed.action}
        >
          {ASSIGNMENT_STATE_INFO.completed.label}
        </Menu.Item>
      )}
      {assignment.canCancel.value && (
        <Link to={cancelAssignmentDrawerUrl}>
          <Menu.Item
            key={ASSIGNMENT_STATE_INFO.cancelled.label}
            action={ASSIGNMENT_STATE_INFO.cancelled.action}
          >
            {ASSIGNMENT_STATE_INFO.cancelled.label}
          </Menu.Item>
        </Link>
      )}
    </Menu>
  );

  return (
    <State assignment={assignment} statesMenu={menu} fontSize={fontSize} />
  );
}

function confirmClearAssignmentWithPendingItems({ onConfirm }) {
  Modal.confirm({
    title: 'Clear Staff Despite Pending Items?',
    content:
      'Confirming will mark the worker as cleared but items will still remain as “pending upload”.',
    onOk: onConfirm,
  });
}

// TODO - Reuse fragment from .graphql file once this gets merged:
// https://github.com/apollographql/graphql-tag/pull/257
AssignmentStateMenu.fragment = gql`
  fragment AssignmentStateMenuFragment on StaffingRequestAssignment {
    id
    state

    worksList {
      id
      completedItemsCount
      totalItemsCount
    }

    worksProfile {
      id
      identificationCode
    }

    canOnboard {
      value
    }

    canOverrideClear {
      value
    }

    canViewClear {
      value
    }

    canWork {
      value
    }

    canComplete {
      value
    }

    canCancel {
      value
    }

    canUpdate {
      value
    }
  }
`;

AssignmentStateMenu.propTypes = {
  state: PropTypes.string,
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    neverStarted: PropTypes.bool.isRequired,
    worksList: PropTypes.shape({
      state: PropTypes.string,
      completedItemsCount: PropTypes.number,
      totalItemsCount: PropTypes.number,
    }),

    canViewClear: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canOverrideClear: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),

    canOnboard: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canWork: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canComplete: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canCancel: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canReset: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canUpdate: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
  }),
  fontSize: PropTypes.string,
};
